// ─────────────────────────────────────────────
//  Facility Rules panel (Phase 5.4) — per-facility behavior knobs, editable
//  and toggleable in Setup, read by the app at runtime (never hard-coded):
//    • supervision ratio (1–4 rooms per doctor → overload flag)
//    • break rules (max window minutes, simultaneous windows per doctor)
//    • room-condition escalation (also notify the day's AIC)
//    • float permission (cross-facility picker gate)
//  Admin-only writes (facilities update rule). Stored under facility.rules,
//  resolved through domain/facilityRules.resolveFacilityRules.
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { updateFacility } from "../firebase/firestore";
import { useFacility } from "../hooks/useFacility";
import { resolveFacilityRules, breakMinuteOptions } from "../domain/facilityRules";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

function RuleRow({ label, help, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 0", borderTop: `1px solid ${color.border.default}`, flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: color.text.primary }}>{label}</div>
        <div style={{ fontSize: 11, color: color.text.muted }}>{help}</div>
      </div>
      {children}
    </div>
  );
}

export function FacilityRulesPanel({ facilityId }) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const { facility, loading } = useFacility(facilityId);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(resolveFacilityRules(facility));
  }, [facility, facilityId]);

  if (loading || !form) return null;

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await updateFacility(facilityId, { rules: form });
      toast.success("Facility rules saved — the app reads them immediately.");
    } catch (err) {
      console.error("Save facility rules failed:", err);
      toast.error("Could not save rules. Admin rights are required.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.panel}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <div style={s.panelTitle}>⚖️ Facility Rules</div>
        {isAdmin && (
          <button style={s.primaryBtn} onClick={save} disabled={saving}>
            {saving ? "Saving…" : "💾 Save Rules"}
          </button>
        )}
      </div>
      <p style={{ fontSize: 12, color: color.text.muted, marginTop: 0, marginBottom: 6 }}>
        Behavior knobs for this facility — read live by the board, break approval, conditions, and floating. Admin only.
      </p>

      <RuleRow label="Supervision ratio" help="Rooms one doctor may supervise before the board flags overload.">
        <select style={s.select} disabled={!isAdmin} value={form.supervisionRatio}
          onChange={(e) => set("supervisionRatio", Number(e.target.value))} aria-label="Supervision ratio">
          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} room{n > 1 ? "s" : ""}</option>)}
        </select>
      </RuleRow>

      <RuleRow label="Max break window" help="Longest approvable break; caps the approve dialog's minutes.">
        <select style={s.select} disabled={!isAdmin} value={form.breakMaxMinutes}
          onChange={(e) => set("breakMaxMinutes", Number(e.target.value))} aria-label="Max break window">
          {[15, 20, 30, 45, 60, 90, 120].map((n) => <option key={n} value={n}>{n} minutes</option>)}
        </select>
      </RuleRow>

      <RuleRow label="Simultaneous breaks per doctor" help="How many of one doctor's rooms may run approved breaks at once.">
        <select style={s.select} disabled={!isAdmin} value={form.breakSimultaneousPerDoctor}
          onChange={(e) => set("breakSimultaneousPerDoctor", Number(e.target.value))} aria-label="Simultaneous breaks per doctor">
          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </RuleRow>

      <RuleRow label="Room-condition escalation" help="Also notify the day's AIC when a CRNA posts a room condition.">
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: color.text.primary }}>
          <input type="checkbox" disabled={!isAdmin} checked={form.notifyAicOnCondition}
            onChange={(e) => set("notifyAicOnCondition", e.target.checked)} />
          Notify AIC too
        </label>
      </RuleRow>

      <RuleRow label="Cross-facility floats" help="Allow pulling staff from other facilities onto this day board.">
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: color.text.primary }}>
          <input type="checkbox" disabled={!isAdmin} checked={form.floatAllowed}
            onChange={(e) => set("floatAllowed", e.target.checked)} />
          Floats allowed
        </label>
      </RuleRow>

      <div style={{ fontSize: 11, color: color.text.muted, marginTop: 8 }}>
        Current cap on the approve dialog: {breakMinuteOptions(form.breakMaxMinutes).join(" / ")} min.
      </div>
    </div>
  );
}

export default FacilityRulesPanel;
